import { useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/features/auth/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import DashboardLayout from "@/features/dashboard/DashboardLayout";
import PublicLayout from "@/features/landing/PublicLayout";
import PublicNav from "@/features/landing/PublicNav";
import { AppSidebar } from "@/features/dashboard/AppSidebar";
import { Loader2 } from "lucide-react";

// First-paint critical: kept eager. These render on the most common entry
// paths (signed-in dashboard, signed-out tools page, login flow).
import Login from "@/features/auth/LoginPage";
import Home from "@/features/landing/HomePage";
import Tools from "@/features/tools/ToolsPage";
import Dashboard from "@/features/dashboard/DashboardPage";
import NotFound from "@/NotFound";

// Everything else is code-split. Each lazy() becomes its own chunk that
// loads only when the route is visited. Cuts the initial JS payload by
// ~60-75% by deferring jspdf, recharts, react-simple-maps, framer-motion-
// heavy admin pages, and the deep content-library hierarchy.
const ForgotPassword = lazy(() => import("@/features/auth/ForgotPasswordPage"));
const ResetPassword = lazy(() => import("@/features/auth/ResetPasswordPage"));
const ToolsV2 = lazy(() => import("@/features/tools/ToolsPageV2"));
const PublicToolLibraryPage = lazy(() => import("@/features/tools/PublicToolLibraryPage"));
const ToolPage = lazy(() => import("@/features/tools/ToolPage"));
const Apply = lazy(() => import("@/features/landing/ApplyPage"));
const Calculators = lazy(() => import("@/features/tools/CalculatorsPage"));
const Mentors = lazy(() => import("@/features/landing/MentorsPage"));
const ClarityCall = lazy(() => import("@/features/dashboard/ClarityCallPage"));
const CalendarPage = lazy(() => import("@/features/dashboard/CalendarPage"));
const Resources = lazy(() => import("@/features/resources/ResourcesPage"));
const ContentLibrary = lazy(() => import("@/features/content-library/ContentLibraryPage"));
const Profile = lazy(() => import("@/features/profile/ProfilePage"));
const Admin = lazy(() => import("@/features/admin/AdminPage"));
const AdminMembersPage = lazy(() => import("@/features/admin/AdminMembersPage"));
const AdminFastTrackPage = lazy(() => import("@/features/admin/AdminFastTrackPage"));
const AdminKpiPage = lazy(() => import("@/features/admin/AdminKpiPage"));
const AdminBugReportsPage = lazy(() => import("@/features/admin/AdminBugReportsPage"));
const AdminToolSuggestionsPage = lazy(() => import("@/features/admin/AdminToolSuggestionsPage"));
const AdminFastTrackFeedbackPage = lazy(() => import("@/features/admin/AdminFastTrackFeedbackPage"));
const AdminSettingsPage = lazy(() => import("@/features/admin/AdminSettingsPage"));
const AdminAnalyticsPage = lazy(() => import("@/features/admin/AdminAnalyticsPage"));
const AdminBlockersPage = lazy(() => import("@/features/admin/AdminBlockersPage"));
const AdminMentors = lazy(() => import("@/features/admin/AdminMentorsPage"));
const RefLeadsPage = lazy(() => import("@/features/admin/RefLeadsPage"));
const SavedDeals = lazy(() => import("@/features/profile/SavedDealsPage"));
const CoursePage = lazy(() => import("@/features/content-library/CoursePage"));
const CourseLessonPage = lazy(() => import("@/features/content-library/CourseLessonPage"));
const FastTrackRecordingsPage = lazy(() => import("@/features/content-library/FastTrackRecordingsPage"));
const Section8Analyzer = lazy(() => import("@/features/tools/section8/Section8AnalyzerPage"));
const FFDPage = lazy(() => import("@/features/financial-freedom-declaration/FFDPage"));
const StrategyFinder = lazy(() => import("@/features/tools/strategy-finder/StrategyFinderPage"));
const StrategyFinderAdmin = lazy(() => import("@/features/admin/StrategyFinderAdminPage"));
const PersonalCashFlowCalculator = lazy(() => import("@/features/tools/PersonalCashFlowCalculator"));
const WelcomePage = lazy(() => import("@/features/migration/WelcomePage"));
const ActionTrackerPage = lazy(() => import("@/features/action-tracker/ActionTrackerPage"));
const RoadmapPage = lazy(() => import("@/features/roadmap/RoadmapPage"));
// CrystalClearCriteriaPage hidden: feature is being rebuilt. Route disabled below.
// const CrystalClearCriteriaPage = lazy(() => import("@/features/crystal-clear/CrystalClearCriteriaPage"));
const HomePageIndex = lazy(() => import("@/features/landing/home/HomePageIndex"));

// Centralised Suspense fallback so all lazy boundaries share the same
// loading shimmer instead of each rendering its own ad-hoc spinner.
function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected route — requires Supabase session
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// Optional auth route — renders children even without session (for public pages with auth gates)
const ProtectedRouteOptional = ({ children }: { children: React.ReactNode }) => {
  const { isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  return <>{children}</>;
};

// Admin route guard
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, loading } = useUserRole();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/calculators" replace />;
  return <>{children}</>;
};

// Home route — redirect authenticated members/admins to the dashboard
const HomeRoute = () => {
  const { session, isLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  if (isLoading || (session && roleLoading)) return <PageFallback />;
  if (session && role !== 'free') return <Navigate to="/dashboard" replace />;
  return <Tools />;
};

// Login route — redirect authenticated users by role, honoring any pending analysis return.
// Also gates migrated members to /welcome until they complete the placement quiz.
const LoginRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, user, migrationStatusLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();

  if (!session) return <>{children}</>;

  const returnTo = localStorage.getItem('pendingAnalysisReturn');
  if (returnTo) {
    localStorage.removeItem('pendingAnalysisReturn');
    return <Navigate to={returnTo} replace />;
  }

  if (roleLoading || migrationStatusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (role !== 'free' && user?.migrationStatus === 'pending_first_login') {
    return <Navigate to="/welcome" replace />;
  }

  return <Navigate to={role === 'free' ? '/calculators' : '/dashboard'} replace />;
};

// Market Finder — show with PublicNav for guests, DashboardLayout for logged-in users
const MarketFinderRoute = () => {
  const { session, isLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (session) {
    return (
      <div className="h-dvh flex w-full overflow-hidden">
        <AppSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <main className="flex-1 overflow-auto p-4 sm:p-6">
            <Section8Analyzer />
          </main>
        </div>
      </div>
    );
  }
  // Public: full-width with public nav
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <PublicNav />
      <main className="flex-1 p-3 sm:p-6 min-w-0">
        <Section8Analyzer />
      </main>
    </div>
  );
};

// Cash Flow route — redirects logged-in users to the dashboard version
const CashflowRoute = () => {
  const { session, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (session) return <Navigate to="/dashboard/cashflow-calculator" replace />;
  return <PersonalCashFlowCalculator />;
};

// Full-screen layout for tools that manage their own internal scroll (no footer, h-screen)
const FullBleedLayout = () => (
  <div className="flex h-dvh flex-col overflow-hidden">
    <PublicNav />
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <Outlet />
    </div>
  </div>
);

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Full-bleed tools — own internal scroll, no footer */}
            <Route element={<FullBleedLayout />}>
              <Route path="/tools/rental-analyzer" element={<ToolPage />} />
              <Route path="/tools/str-calculator" element={<ToolPage />} />
            </Route>
            {/* Public marketing pages */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/firstdeal" element={<Home />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools-preview" element={<ToolsV2 />} />
              <Route path="/tools/:slug" element={<ToolPage />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/cashflow-calculator" element={<CashflowRoute />} />
            </Route>
            <Route path="/login" element={<LoginRoute><Login /></LoginRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Protected member hub */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/tools" element={<Tools />} />
              <Route path="/dashboard/tools/:slug" element={<ToolPage />} />
              <Route path="/mentors" element={<Mentors />} />
              <Route path="/clarity-call" element={<ClarityCall />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/content" element={<ContentLibrary />} />
              <Route path="/calculators" element={<Calculators />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="/admin/members" element={<AdminRoute><AdminMembersPage /></AdminRoute>} />
              <Route path="/admin/fast-track" element={<AdminRoute><AdminFastTrackPage /></AdminRoute>} />
              <Route path="/admin/kpi" element={<AdminRoute><AdminKpiPage /></AdminRoute>} />
              <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
              <Route path="/admin/bugs" element={<AdminRoute><AdminBugReportsPage /></AdminRoute>} />
              <Route path="/admin/tool-suggestions" element={<AdminRoute><AdminToolSuggestionsPage /></AdminRoute>} />
              <Route path="/admin/feedback" element={<AdminRoute><AdminFastTrackFeedbackPage /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
              <Route path="/admin/blockers" element={<AdminRoute><AdminBlockersPage /></AdminRoute>} />
              <Route path="/admin/mentors" element={<AdminRoute><AdminMentors /></AdminRoute>} />
              <Route path="/admin/ref-leads" element={<AdminRoute><RefLeadsPage /></AdminRoute>} />
              <Route path="/saved-deals" element={<SavedDeals />} />
              <Route path="/content/fast-track" element={<FastTrackRecordingsPage />} />
              <Route path="/content/course" element={<CoursePage />} />
              <Route path="/content/course/:slug" element={<CoursePage />} />
              <Route path="/content/course/:slug/:moduleId" element={<CourseLessonPage />} />
              <Route path="/content/course/:slug/:moduleId/:lessonId" element={<CourseLessonPage />} />
              {/* <Route path="/crystal-clear-criteria" element={<CrystalClearCriteriaPage />} /> */}
              <Route path="/financial-freedom-declaration" element={<FFDPage />} />
              <Route path="/habits" element={<Navigate to="/dashboard" replace />} />
              <Route path="/action-tracker" element={<ActionTrackerPage />} />
              <Route path="/dashboard/cashflow-calculator" element={<PersonalCashFlowCalculator />} />
              <Route path="/roadmap" element={<RoadmapPage />} />
            </Route>
            {/* Market Finder — public with interaction auth gate, full layout for logged-in users */}
            <Route path="/marketfinder" element={<MarketFinderRoute />} />
            <Route path="/rtpanalyzer" element={<Navigate to="/marketfinder" replace />} />
            <Route element={<PublicLayout />}>
              <Route path="/strategy-finder" element={<StrategyFinder />} />
            </Route>
            {/* Strategy Finder Admin — inside dashboard layout, admin role required */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/strategy-finder-admin" element={<AdminRoute><StrategyFinderAdmin /></AdminRoute>} />
            </Route>
            {/* Standalone /home marketing page — noindex, no shared layout */}
            <Route path="/home" element={<HomePageIndex />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
